import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import { func, PropTypes, shape } from 'prop-types';

import { formatNumber } from 'utils/helpers/format';
import PersistentDataGrid from 'common/PersistentDataGrid';
import NoSourceLocationsAvailable from '../NoSourceLocationsAvailable';
import NoInventoryForBarrels from '../NoInventoryForBarrels';
import LocationDetailsDrawer from '../LocationDetailsDrawer';
import GridHeader from '../GridHeader';
import getSafeReconsignmentState from '../getSafeReconsignmentState';
import createLocationColumns from './columnDefinitions';
import useLocationsData from '../useLocationsData';

export default function FromLocationsGrid({ reconsignmentState, onLocationSelect }) {
  const [selectedLocationId, setSelectedLocationId] = useState(
    reconsignmentState.locationId || null
  );
  const [selectedLocationDetails, setSelectedLocationDetails] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Effect to clear selected location ID when reconsignment state changes
  useEffect(() => {
    setSelectedLocationId(reconsignmentState.locationId || null);
  }, [
    reconsignmentState.inventoryOwner,
    reconsignmentState.product,
    reconsignmentState.rvp,
    reconsignmentState.origin,
    reconsignmentState.inventoryStatus,
    reconsignmentState.consignmentStatus,
    reconsignmentState.locationId,
  ]);

  // Extract safe values from reconsignmentState
  const safeReconsignmentState = useMemo(
    () => getSafeReconsignmentState(reconsignmentState),
    [reconsignmentState]
  );

  // Use the custom hook for location data fetching and management
  const { locationsData, isLoading, hasLocations, noInventoryForBarrels } =
    useLocationsData(safeReconsignmentState);

  // Check if we have a product selected
  const hasProduct = !!safeReconsignmentState.product?.productId;

  // Handle location selection
  const handleLocationSelect = useCallback(
    locationId => {
      // If the same location is selected again, clear the selection
      if (locationId === selectedLocationId) {
        setSelectedLocationId(null);
        onLocationSelect(null);
      } else {
        setSelectedLocationId(locationId);
        // Find the complete location object to pass to the parent component
        const selectedLocation = locationsData?.find(
          location => location.locationIdn === locationId
        );
        if (selectedLocation) {
          onLocationSelect(selectedLocation);
        }
      }
    },
    [locationsData, onLocationSelect, selectedLocationId]
  );

  // Handle drawer close
  const handleCloseDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedLocationDetails(null);
  }, []);

  // Handle view details button click
  const handleViewDetails = useCallback(
    row => {
      setSelectedLocationDetails({
        ...row,
        inventoryOwner: safeReconsignmentState.inventoryOwner?.customerNameShort,
        product: safeReconsignmentState.product?.productName,
        rvp: safeReconsignmentState.rvp || 'Any',
        volume: formatNumber(row.totalVolume),
      });

      setDrawerOpen(true);
    },
    [safeReconsignmentState]
  );

  // Grid column definitions - using imported column definitions utility
  const columns = useMemo(
    () =>
      createLocationColumns({
        handleLocationSelect,
        handleViewDetails,
        safeReconsignmentState,
        selectedLocationIdn: selectedLocationId,
      }),
    [selectedLocationId, handleLocationSelect, handleViewDetails, safeReconsignmentState]
  );

  // Define consistent box styling
  const boxStyles = {
    '& > *': {
      flex: 1,
      width: '100%',
    },
    alignItems: 'stretch',
    display: 'flex',
    height: '100%',
    justifyContent: 'stretch',
    width: '100%',
  };

  // Render UI based on state
  if (noInventoryForBarrels && hasProduct) {
    return (
      <Box sx={boxStyles}>
        <NoInventoryForBarrels />
      </Box>
    );
  }

  if (!isLoading && !hasLocations && hasProduct && !noInventoryForBarrels) {
    return (
      <Box sx={boxStyles}>
        <NoSourceLocationsAvailable />
      </Box>
    );
  }

  return (
    <Box sx={{ px: 4 }}>
      <GridHeader
        description="Select the location from where you would like to move barrels for reconsignment"
        title="Select From Location"
      />
      <PersistentDataGrid
        columns={columns}
        getRowId={row => row.locationIdn}
        loading={isLoading}
        rows={locationsData || []}
        sx={{
          '& .MuiDataGrid-footerContainer': {
            borderTop: 'none',
          },

          // Toolbar alignment fix
          '& .MuiDataGrid-toolbarContainer': {
            marginLeft: '0 !important',
            paddingLeft: '0 !important',
          },
        }}
      />
      {/* Location details drawer */}
      {selectedLocationDetails && (
        <LocationDetailsDrawer
          location={{
            ...selectedLocationDetails,
            rawVolume: selectedLocationDetails.totalVolume,
          }}
          onClose={handleCloseDrawer}
          open={drawerOpen}
          reconsignmentState={reconsignmentState}
        />
      )}
    </Box>
  );
}

FromLocationsGrid.propTypes = {
  onLocationSelect: func,
  reconsignmentState: shape({
    inventoryOwner: shape({}),
    origin: PropTypes.oneOfType([PropTypes.array, PropTypes.shape({})]),
    product: shape({}),
    rvp: PropTypes.string,
  }).isRequired,
};

FromLocationsGrid.defaultProps = {
  onLocationSelect: () => {},
};
